/**
 * Personal usage frequencies for Quick Add suggestions.
 * One localStorage key; reads served from module cache, writes debounced.
 */

const STORAGE_KEY = "quick-add-usage";
const VERSION = 1;
const MAX_LINES = 30;
const DECAY_PER_DAY = 0.95;
const WRITE_DELAY_MS = 500;
const DAY_MS = 86400000;

let cache = null;
let writeTimer = 0;

const emptyData = () => ({ v: VERSION, contracts: {}, structures: {}, lines: {} });

function load() {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const data = raw ? JSON.parse(raw) : null;
    cache =
      data &&
      data.v === VERSION &&
      data.contracts &&
      data.structures &&
      data.lines
        ? data
        : emptyData();
  } catch {
    cache = emptyData();
  }
  return cache;
}

function scheduleWrite() {
  clearTimeout(writeTimer);
  writeTimer = setTimeout(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
    } catch {
      // Storage unavailable — suggestions just won't survive a reload.
    }
  }, WRITE_DELAY_MS);
}

/** Frequency with recency decay so habits you dropped months ago sink. */
function score(entry, now) {
  const days = Math.max(0, (now - (entry.t || 0)) / DAY_MS);
  return (entry.n || 0) * Math.pow(DECAY_PER_DAY, days);
}

function bump(map, key, now) {
  if (!key) return;
  const entry = map[key] || { n: 0, t: now };
  entry.n += 1;
  entry.t = now;
  map[key] = entry;
}

/**
 * Record a successfully staged line. Increments the contract token, the
 * structure label, and stores the normalized line as a reusable template.
 */
export function recordUsage(parsed, rawLine, now = Date.now()) {
  const data = load();
  bump(data.contracts, parsed?.contract?.token, now);
  bump(data.structures, String(parsed?.label || "").trim().toLowerCase(), now);

  const line = String(rawLine || "").replace(/\s+/g, " ").trim();
  if (line) {
    const key = line.toLowerCase();
    bump(data.lines, key, now);
    data.lines[key].raw = line;
    const keys = Object.keys(data.lines);
    if (keys.length > MAX_LINES) {
      let worstKey = null;
      let worstScore = Infinity;
      keys.forEach((k) => {
        const s = score(data.lines[k], now);
        if (s < worstScore) {
          worstScore = s;
          worstKey = k;
        }
      });
      if (worstKey) delete data.lines[worstKey];
    }
  }
  scheduleWrite();
}

/**
 * Sorted usage lists (highest score first).
 * Each item: { key, raw, score } — `raw` preserves original casing for lines.
 */
export function getRankings(now = Date.now()) {
  const data = load();
  const rank = (map) =>
    Object.entries(map)
      .map(([key, entry]) => ({ key, raw: entry.raw || key, score: score(entry, now) }))
      .sort((a, b) => b.score - a.score);
  return {
    contracts: rank(data.contracts),
    structures: rank(data.structures),
    lines: rank(data.lines),
  };
}

/** Test helper: drop module cache and pending writes. */
export function __resetUsageForTests() {
  cache = null;
  clearTimeout(writeTimer);
  writeTimer = 0;
}
